/**
 * Client for a brand's OWN game lists: the lists the owner built themselves, which have no platform
 * counterpart on the site.
 *
 * That absence is the whole reason this is a separate route family rather than another
 * `TenantSourceKind`. A tenant source OVERRIDES a system source, so wl-data requires a
 * `system_source_id` that exists and whose template allows tenant sources; a list the owner invented
 * overrides nothing, and there is no system source to name. So these lists live in spark-creatives'
 * own store and a page references one by the `source_id` the backend derives from its row id.
 *
 * The backend contract is `/creatives/casino-instance/{id}/owner-game-lists` in
 * `spark-creatives/pkg/swagger/swagger.yml`. The brand is named by the path, never by the body: the
 * ownership middleware validates that casino instance and the handlers take the tenant from it.
 */
import axios from 'axios';

import { creativesRequestConfig, creativesServiceBase, creativesServiceConfigured } from './creative-api';
import { idList, tenantSourceResult, type TenantSourceResult } from './creatives-tenant-source';
import type { DsiSourceInfo } from './live-preview';
import { dropPageSections, type OwnerSectionKind } from './page-arrangement-api';
import { resolveGamesByIds, type GameSourceRecord } from './game-sources';
import type { ListFilters } from './game-filters';
import { gameListsFromSources, type GamePage, type SiteGameList, type SiteSourceContent } from './site-games';
import { dropSitePages } from './site-pages-api';

/** How one of the brand's own lists shows on a page: a carousel (one row that scrolls) or a grid. The
 *  kinds of section the page renders it as, by the same names. */
export type OwnListKind = OwnerSectionKind;

/**
 * The shapes an owner picks from, in their words: a Row (the site's carousel: one row that scrolls), a Grid
 * (large thumbnails, the site's plain grid) and a Mixed grid (the site's `cdr-01` grid: small thumbnails with
 * the first and the twentieth game large). A shape is a kind of section and, for a grid, its layout.
 */
export type ListShape = 'row' | 'grid' | 'mixed';

/** The site's mixed grid layout. The grid element type declares four more, which the hub does not offer: on
 *  the sites seen, only this one and `pldt-01` are styled, and the rest render as the plain grid. */
export const MIXED_GRID_LAYOUT = 'cdr-01';

/** The shape a stored kind and layout make. A grid in a layout the hub does not offer reads as a Grid. */
export const shapeOf = (kind: OwnListKind, layout: string): ListShape =>
  kind === 'games_carousel' ? 'row' : layout === MIXED_GRID_LAYOUT ? 'mixed' : 'grid';

/** The kind and layout a shape is stored and rendered as. */
export const sectionOf = (shape: ListShape): { kind: OwnListKind; layout: string } =>
  shape === 'row'
    ? { kind: 'games_carousel', layout: '' }
    : { kind: 'games_grid', layout: shape === 'mixed' ? MIXED_GRID_LAYOUT : '' };

/** How a list is built: `manual`, the games the owner picked, or `auto`, every catalogue game its filters
 *  match (see `resolveAutoList`), which the site keeps current as the catalogue grows. */
export type OwnListMode = 'manual' | 'auto';

/** One of the brand's own lists, as stored. `game_ids` is what the owner is editing (the draft);
 *  `published_game_ids` is what the site shows, null when the service does not say. `source_id` is how a
 *  page section references it, and its address on the site (`/games/<source_id>`). `layout` is a grid's
 *  (see `ListShape`), empty for the plain grid and for a carousel. */
export interface OwnerGameList {
  id: string;
  page: GamePage;
  name: string;
  /** The name in each language of the brand's website (language id -> name, "en" the site's fallback), as the page
   *  publishes it for the list's title; null (or absent) when the list has none, and the site shows `name` in every
   *  language. */
  names?: ListNames | null;
  game_ids: string[];
  published_game_ids: string[] | null;
  source_id: string;
  kind: OwnListKind;
  layout: string;
  /** How the list is built, as the owner is editing it (the draft). */
  mode: OwnListMode;
  /** An automatic list's filters, as the owner is editing them; null for a hand-picked list. For an automatic
   *  list `game_ids` is the owner's order of its games, empty until they reorder it. */
  filters: ListFilters | null;
  /** The mode and filters the site shows, null when the service does not say (a list published before lists
   *  had a mode was hand-picked). */
  published_mode: OwnListMode | null;
  published_filters: ListFilters | null;
}

/** A list's name by website language id ("en", "mx", "br"...). */
export type ListNames = Record<string, string>;

/** A list's names as the service sends them, kept to non-empty strings; null when there are none. */
export const namesOf = (value: unknown): ListNames | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const names = Object.fromEntries(
    Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1] !== ''),
  );
  return Object.keys(names).length > 0 ? names : null;
};

/** The source id the service gave every list made before lists had addresses: `owner-list-<id>`. */
const LEGACY_OWN_LIST_SOURCE_PREFIX = 'owner-list-';

/** Whether a list's source id is one of the SITE's lists, by `siteGameLists`' rule: not one `ownSourceIds` names, nor
 *  one an older list of the brand's goes by (its prefix). For a list known by its id alone, as the service names the
 *  lists a page shows. */
export const isSiteGameListId = (id: string, ownSourceIds: ReadonlySet<string>): boolean =>
  !ownSourceIds.has(id) && !id.startsWith(LEGACY_OWN_LIST_SOURCE_PREFIX);

/**
 * The page's game lists that are the SITE's own, as the site reports them.
 *
 * A page showing one of the brand's own lists makes the site report that list's source like any other, and
 * it is never a list of the site's. A new list's source id is a slug of its name, so nothing in its form says
 * whose it is: `ownSourceIds` names the brand's lists, and must include those deleted since the page loaded,
 * because the frame reports what the page held then (a list deleted since was drawn as a site list under
 * the same name). An older list's id says it by its prefix.
 */
export const siteGameLists = (sources: DsiSourceInfo[], ownSourceIds: ReadonlySet<string>): SiteGameList[] =>
  gameListsFromSources(sources).filter((list) => isSiteGameListId(list.id, ownSourceIds));

/** One of the owner's lists as the site preview needs it: the source the page renders it by, and the games
 *  it holds as the owner is editing them. */
export type OwnListInPreview = { sourceId: string; ids: string[] };

/**
 * What the site preview is handed for the owner's lists. The preview draws a list from what the site serves,
 * which is its PUBLISHED games, and lays a draft order over that. For the owner's lists it is handed each whole
 * list instead, under its source id: its working games resolved from the catalogue, in the working order. An
 * order laid over the published games is not enough, because an edit adds and drops games, and a game the draft
 * dropped would come back at the end of the list.
 *
 * Every list gets its games, so the preview never falls back to what it read from the site before a publish;
 * only the lists in `waiting` (their games have unpublished changes) carry a draft. Without `waiting`, all do.
 *
 * `catalog` must hold FULL game records (art and all, the catalogue read without a view): the preview pins each list
 * into the framed site as its whole content, which skips the site's own hydration, so a slim record (the tab's
 * catalogue, see `useGameSources`) would be drawn as a blank tile.
 */
export const ownListsInPreview = (
  lists: OwnListInPreview[],
  catalog: readonly GameSourceRecord[],
  waiting?: ReadonlySet<string>,
): { drafts: Record<string, string[]>; contents: Record<string, SiteSourceContent> } => {
  const drafts: Record<string, string[]> = {};
  const contents: Record<string, SiteSourceContent> = {};
  for (const { sourceId, ids } of lists) {
    if (!waiting || waiting.has(sourceId)) drafts[sourceId] = ids;
    contents[sourceId] = { data: resolveGamesByIds(ids, catalog) };
  }
  return { drafts, contents };
};

/** A list as the hub sends it: no `id` creates, an `id` updates that list. The games are stored in
 *  the order given, so the order IS the value. An update is a DRAFT unless `publish` is set, which also puts
 *  the games on the site; a new list is always written to the site (see the swagger). */
export interface OwnerGameListDraft {
  id?: string;
  page: GamePage;
  name: string;
  /** Sent only when they change (a new or renamed list, a name edited in one language): absent keeps the list's, and
   *  an empty object clears them. */
  names?: ListNames;
  game_ids: string[];
  kind: OwnListKind;
  layout: string;
  mode: OwnListMode;
  /** Sent for an automatic list; null for a hand-picked one. */
  filters: ListFilters | null;
  publish?: boolean;
}

/** What a save returns: the row's id and the `source_id` a page section references it by. */
export interface OwnerGameListSaved {
  id: string;
  source_id: string;
}

const ownerGameListsPath = (casinoInstanceId: string): string =>
  `${creativesServiceBase()}/creatives/casino-instance/${encodeURIComponent(casinoInstanceId)}/owner-game-lists`;

/** A list's page as the service names it: its path. The two names the hub used before pages were paths are
 *  read as the pages they meant, for a service that has not moved its rows yet. */
const pageOf = (value: unknown): GamePage => {
  if (value === 'casino') return '/casino';
  return typeof value === 'string' && value.startsWith('/') ? value : '/';
};

/** A list's shape as the service names it; anything else (a service that does not say) is a carousel, the
 *  shape every list had before lists had one. */
const kindOf = (value: unknown): OwnListKind => (value === 'games_grid' ? 'games_grid' : 'games_carousel');

/** A list's mode as the service names it; anything else (a service that does not say) is hand-picked, the only
 *  kind of list there was before lists had a mode. */
const modeOf = (value: unknown): OwnListMode => (value === 'auto' ? 'auto' : 'manual');

/** A list's filters as the service sends them, each facet kept to its string picks; null when there are none to
 *  read. */
const filtersOf = (value: unknown): ListFilters | null => {
  if (typeof value !== 'object' || value === null) return null;
  const body = value as Record<string, unknown>;
  const picks = (facet: unknown): string[] =>
    Array.isArray(facet) ? facet.filter((pick): pick is string => typeof pick === 'string' && pick !== '') : [];
  return { providers: picks(body.providers), types: picks(body.types), themes: picks(body.themes) };
};

/** Parse one stored list. A row whose `page` the hub does not know reads as `homepage`, the page
 *  every brand has, rather than throwing: an unknown page would otherwise hide the whole response. */
export const parseOwnerGameList = (body: Record<string, unknown>): OwnerGameList => ({
  id: typeof body.id === 'string' ? body.id : '',
  page: pageOf(body.page),
  name: typeof body.name === 'string' ? body.name : '',
  names: namesOf(body.names),
  game_ids: idList(body.game_ids),
  published_game_ids: Array.isArray(body.published_game_ids) ? idList(body.published_game_ids) : null,
  source_id: typeof body.source_id === 'string' ? body.source_id : '',
  kind: kindOf(body.kind),
  layout: kindOf(body.kind) === 'games_grid' && typeof body.layout === 'string' ? body.layout : '',
  mode: modeOf(body.mode),
  filters: modeOf(body.mode) === 'auto' ? filtersOf(body.filters) : null,
  published_mode: body.published_mode === 'auto' || body.published_mode === 'manual' ? body.published_mode : null,
  published_filters: body.published_mode === 'auto' ? filtersOf(body.published_filters) : null,
});

/** Parse a list response, dropping rows with no id: one cannot be edited or deleted, and a page
 *  section could not reference it. */
export const parseOwnerGameLists = (body: Record<string, unknown>): OwnerGameList[] =>
  (Array.isArray(body.data) ? body.data : [])
    .filter((row): row is Record<string, unknown> => typeof row === 'object' && row !== null)
    .map(parseOwnerGameList)
    .filter((list) => list.id !== '');

/** The brand's own lists, newest arrangement first. `page` narrows to one site page. */
export const getOwnerGameLists = async (
  casinoInstanceId: string,
  page?: GamePage,
  signal?: AbortSignal,
): Promise<TenantSourceResult<OwnerGameList[]>> => {
  if (!creativesServiceConfigured()) return { ok: false, reason: 'unavailable' };
  try {
    const query = page ? `?page=${encodeURIComponent(page)}` : '';
    const response = await axios.get(`${ownerGameListsPath(casinoInstanceId)}${query}`, creativesRequestConfig(signal));
    return tenantSourceResult(response.status, response.data, parseOwnerGameLists);
  } catch {
    return { ok: false, reason: 'failed' };
  }
};

/** Create or update one of the brand's own lists. */
export const saveOwnerGameList = async (
  casinoInstanceId: string,
  draft: OwnerGameListDraft,
  signal?: AbortSignal,
): Promise<TenantSourceResult<OwnerGameListSaved>> => {
  if (!creativesServiceConfigured()) return { ok: false, reason: 'unavailable' };
  try {
    const response = await axios.post(
      ownerGameListsPath(casinoInstanceId),
      {
        id: draft.id ?? '',
        page: draft.page,
        name: draft.name,
        ...(draft.names ? { names: draft.names } : {}),
        game_ids: draft.game_ids,
        kind: draft.kind,
        layout: draft.layout,
        mode: draft.mode,
        filters: draft.mode === 'auto' ? draft.filters : null,
        // Only when asked: without it an update is a draft, and the site keeps its published games.
        ...(draft.publish ? { publish: true } : {}),
      },
      creativesRequestConfig(signal),
    );
    return tenantSourceResult(response.status, response.data, (body) => ({
      id: typeof body.id === 'string' ? body.id : '',
      source_id: typeof body.source_id === 'string' ? body.source_id : '',
    }));
  } catch {
    return { ok: false, reason: 'failed' };
  }
};

/** Remove one of the brand's own lists. The service takes it off every page that shows it first, live page
 *  included, so no page keeps a section bound to a source that is gone. It publishes those pages again without the
 *  list, so the shared reads of every page of the brand, and its site pages, whose lists follow those pages, are dropped
 *  before the request is sent and again once it settles. */
export const deleteOwnerGameList = async (
  casinoInstanceId: string,
  id: string,
  signal?: AbortSignal,
): Promise<TenantSourceResult<true>> => {
  if (!creativesServiceConfigured()) return { ok: false, reason: 'unavailable' };
  dropPageSections(casinoInstanceId);
  dropSitePages(casinoInstanceId);
  try {
    const response = await axios.delete(
      `${ownerGameListsPath(casinoInstanceId)}/${encodeURIComponent(id)}`,
      creativesRequestConfig(signal),
    );
    return tenantSourceResult(response.status, response.data, () => true as const);
  } catch {
    return { ok: false, reason: 'failed' };
  } finally {
    dropPageSections(casinoInstanceId);
    dropSitePages(casinoInstanceId);
  }
};
